import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { OPERATORS, OPERATORS_MAP } from '../../enums/operatorEnum';
import { SliderFilterV2 } from '../sliderFilter/v2';
import { predefinedDateRangesOptions, PredefinedDates, predefinedOtherOption } from './predefinedDates';
import { Chip, DatePickerInput, Divider, IconButton, Layout, Select, Switch, Typography } from '@hub-mono/ui';
import { customDropdownDateRangesOption, dropdownDateRangesOptions } from '../dateRange/buttonsV2';
import { addDays, DATE_FORMATS, formatDate, isAfter, isBefore } from '@hub-mono/utils';
import styled from 'styled-components';
import { useAppKeyWords } from '../../hooks';
import { useSelector } from 'react-redux';
import { selectNoOfWorkHoursInWeekDays, selectWeekDaysSetting } from '../../selectors/company';

const EMPTY_VALUE = { operator: '', value: '' };
const DEFAULT_VALUE = { operator: OPERATORS[0].value, value: 0 };

const utilizationSliderConfig = {
    min: 0,
    max: 100,
    marks: new Array(11).fill(0).map((_, i) => {
        const val = i * 10;
        return {
            value: val,
            scaledValue: val,
            label: val,
        };
    }),
};

const availabilitySliderConfig = {
    min: 0,
    max: 175,
    marks: [
        {
            value: 0,
            scaledValue: 0,
            label: '0',
        },
        {
            value: 25,
            scaledValue: 25,
            label: '25',
        },
        {
            value: 50,
            scaledValue: 50,
            label: '50',
        },
        {
            value: 75,
            scaledValue: 75,
            label: '75',
        },
        {
            value: 100,
            scaledValue: 100,
            label: '100',
        },
        {
            value: 125,
            scaledValue: 250,
            label: '250',
        },
        {
            value: 150,
            scaledValue: 500,
            label: '500',
        },
        {
            value: 175,
            scaledValue: 1000,
            label: '1000hrs',
        },
    ],
};

const getPredefinedUtilisationOptions = ({ appKeywords }) => [
    {
        key: 'full',
        label: '0%',
        icon: 'user_switch',
        dataCy: 'utilization-option-full',
        description: `${appKeywords.resourcePluralKeyWord} with full availability`,
        values: {
            value: 0,
            operator: OPERATORS_MAP.eq.value,
        },
    },
    {
        key: 'partial',
        label: 'Under',
        icon: 'battery_vertical_low',
        dataCy: 'utilization-option-partial',
        description: `${appKeywords.resourcePluralKeyWord} with partial availability under 100%`,
        values: {
            value: 100,
            operator: OPERATORS_MAP.lt.value,
        },
    },
    {
        key: 'fullyBooked',
        label: '100%',
        icon: 'battery_vertical_full',
        dataCy: 'utilization-option-fully-booked',
        description: `${appKeywords.resourcePluralKeyWord} who are booked to 100% capacity`,
        values: {
            value: 100,
            operator: OPERATORS_MAP.eq.value,
        },
    },
    {
        key: 'overbooked',
        label: 'Over',
        icon: 'battery_warning_vertical',
        dataCy: 'utilization-option-overbooked',
        description: `${appKeywords.resourcePluralKeyWord} booked over 100%`,
        values: {
            value: 100,
            operator: OPERATORS_MAP.gt.value,
        },
    },
];

const getPredefinedAvailabilityOptions = ({
    appKeywords,
    rangeSelectedOption,
    noOfWorkHours,
    dates,
    weekDaysSettings,
}) => {
    let partialAvailabilityValue = 0;

    switch (rangeSelectedOption.value) {
        case 'last-week':
        case 'this-week':
        case 'next-week':
            partialAvailabilityValue = noOfWorkHours;
            break;
        case 'last-month':
        case 'this-month':
        case 'next-month':
            partialAvailabilityValue = noOfWorkHours * 4;
            break;
        case 'last-quarter':
        case 'this-quarter':
        case 'next-quarter':
            partialAvailabilityValue = noOfWorkHours * 4 * 3;
            break;
        case 'last-year':
        case 'this-year':
        case 'next-year':
            partialAvailabilityValue = noOfWorkHours * 4 * 3 * 4;
            break;
        case 'custom':
            if (dates.end && dates.start) {
                let start = new Date(dates.start);
                const end = new Date(dates.end);
                let workHours = 0;

                while (start <= end) {
                    const day = formatDate(start, DATE_FORMATS.DAY_LONG).toLowerCase();

                    if (weekDaysSettings[day].workDay) {
                        workHours += weekDaysSettings[day].minutes / 60;
                    }

                    start = addDays(start, 1);
                }

                partialAvailabilityValue = workHours;
            }
    }

    let rangeDescription = rangeSelectedOption?.label?.toLowerCase() || '';

    if (rangeSelectedOption?.value === 'custom' && dates.end && dates.start) {
        rangeDescription = `${formatDate(dates.start, DATE_FORMATS.YEAR_MONTH_DAY_FORMAT)} - ${formatDate(
            dates.end,
            DATE_FORMATS.YEAR_MONTH_DAY_FORMAT
        )}`;
    }

    return [
        {
            key: 'full',
            label: 'Available',
            dataCy: 'availability-option-available',
            description: `${appKeywords.resourcePluralKeyWord} with greater than 0 hours of availability ${rangeDescription}`,
            icon: 'full_availability',
            iconActive: 'full_availability_white',
            values: {
                value: 0,
                operator: OPERATORS_MAP.gt.value,
            },
        },
        {
            key: 'fullyBooked',
            label: 'Booked',
            icon: 'no_availability',
            iconActive: 'no_availability_white',
            dataCy: 'availability-option-fully-booked',
            description: `${appKeywords.resourcePluralKeyWord} with zero hours available ${rangeDescription}`,
            values: {
                value: 0,
                operator: OPERATORS_MAP.eq.value,
            },
        },
        {
            key: 'overbooked',
            label: 'Over',
            icon: 'negative_availability',
            iconActive: 'negative_availability_white',
            dataCy: 'availability-option-overbooked',
            description: `${appKeywords.resourcePluralKeyWord} booked over ${partialAvailabilityValue} hours ${rangeDescription}`,
            values: {
                value: 0,
                operator: OPERATORS_MAP.lt.value,
            },
        },
    ];
};

const ButtonsWrapper = styled(Layout)`
    width: ${props => (props.isAvailability ? 450 : 563)}px;
    align-self: center;
`;

const StyledIconButtonWrapper = styled(Layout)`
    width: 91px;
`;

const UtilizationIconButton = props => {
    const { availabilityKey, selectedOption, icon, iconActive, onClick, noFill = false, disabled, label, dataCy } = props;
    const isSelected = selectedOption === availabilityKey;

    return (
        <StyledIconButtonWrapper stack hAlign="center" gap="--Spacing-50">
            <IconButton
                dataCy={dataCy}
                disabled={disabled}
                variant={isSelected ? 'filled' : 'outlined'}
                icon={isSelected ? iconActive || icon : icon}
                onClick={() => onClick(availabilityKey)}
                noFill={noFill}
            />
            <Typography variant="label" size="medium">
                {label}
            </Typography>
        </StyledIconButtonWrapper>
    );
};

const CapacityFinder = props => {
    const {
        variant,
        weekType,
        dates,
        onDatesChange,
        utilization,
        availability,
        onUtilizationChanged,
        onAvailabilityChanged,
        onBothFiltersChange,
    } = props;

    const appKeywords = useAppKeyWords();
    const noOfWorkHours = useSelector(selectNoOfWorkHoursInWeekDays);
    const weekDaysSettings = useSelector(selectWeekDaysSetting);

    const [rangeSelectedOption, setRangeSelectedOption] = useState(() => {
        return (
            dropdownDateRangesOptions.find(
                option => option.dateState === dates.dateState && option.periodType === dates.periodType
            ) || customDropdownDateRangesOption
        );
    }, [dates.dateState, dates.periodType]);

    const predefinedUtilisationOptions = useMemo(() => {
        return getPredefinedUtilisationOptions({ appKeywords });
    }, [appKeywords]);

    const predefinedAvailabilityOptions = useMemo(() => {
        return getPredefinedAvailabilityOptions({
            appKeywords,
            noOfWorkHours,
            rangeSelectedOption,
            dates,
            weekDaysSettings,
        });
    }, [appKeywords, dates, noOfWorkHours, rangeSelectedOption, weekDaysSettings]);

    const [state, setState] = useState(() => {
        const hasAvailability = Number.isFinite(availability?.value);
        const isActive = Number.isFinite(availability?.value) || Number.isFinite(utilization?.value);

        let utilizationSelectedOption, availabilitySelectedOption;

        if (isActive) {
            if (hasAvailability) {
                availabilitySelectedOption =
                    predefinedAvailabilityOptions.find(option => {
                        return (
                            option.values.value === availability?.value &&
                            option.values.operator === availability?.operator
                        );
                    })?.key || null;
            } else {
                utilizationSelectedOption =
                    predefinedUtilisationOptions.find(option => {
                        return (
                            option.values.value === utilization?.value &&
                            option.values.operator === utilization?.operator
                        );
                    })?.key || null;
            }
        }

        return {
            isActive,
            finderCase: hasAvailability ? 'availability' : 'utilization',
            predefinedSelectedDate: (
                predefinedDateRangesOptions.find(
                    option => option.dateState === dates.dateState && option.periodType === dates.periodType
                ) || predefinedOtherOption
            ).value,
            utilizationSelectedOption,
            availabilitySelectedOption,
        };
    });

    const { isActive, finderCase } = state;
    const isAvailabilityCase = finderCase === 'availability';

    const handleToggleActive = useCallback(() => {
        setState(prevState => {
            const newIsActive = !prevState.isActive;

            if (!newIsActive) {
                onBothFiltersChange({
                    availability: EMPTY_VALUE,
                    utilization: EMPTY_VALUE,
                });

                return {
                    ...prevState,
                    isActive: newIsActive,
                    utilizationSelectedOption: undefined,
                    availabilitySelectedOption: undefined,
                };
            } else {
                let newAvailabilityValue = EMPTY_VALUE;
                let newUtilisationValue = EMPTY_VALUE;

                let newUtilizationSelectedOption = prevState.utilizationSelectedOption;
                let newAvailabilitySelectedOption = prevState.availabilitySelectedOption;

                if (!prevState.availabilitySelectedOption && !prevState.utilizationSelectedOption) {
                    if (prevState.finderCase === 'availability') {
                        newAvailabilitySelectedOption = predefinedAvailabilityOptions[0].key;
                    } else {
                        newUtilizationSelectedOption = predefinedUtilisationOptions[0].key;
                    }
                }

                if (newAvailabilitySelectedOption) {
                    newAvailabilityValue =
                        predefinedAvailabilityOptions.find(option => option.key === newAvailabilitySelectedOption)
                            ?.values || newAvailabilityValue;
                }

                if (newUtilizationSelectedOption) {
                    newUtilisationValue =
                        predefinedUtilisationOptions.find(option => option.key === newUtilizationSelectedOption)
                            ?.values || newUtilisationValue;
                }

                onBothFiltersChange({
                    availability: newAvailabilityValue,
                    utilization: newUtilisationValue,
                });

                return {
                    ...prevState,
                    isActive: newIsActive,
                    utilizationSelectedOption: newUtilizationSelectedOption,
                    availabilitySelectedOption: newAvailabilitySelectedOption,
                };
            }
        });
    }, [onBothFiltersChange, predefinedAvailabilityOptions, predefinedUtilisationOptions]);

    const handlePredefinedRangeClick = useCallback(
        selectedRange => {
            if (!selectedRange) {
                return;
            }

            if (selectedRange.value === 'other' || !selectedRange.getRange) {
                setState(prev => ({ ...prev, predefinedSelectedDate: selectedRange.value }));
            } else {
                const newDatesState = selectedRange.getRange(weekType);
                onDatesChange({
                    dateState: newDatesState.dateState,
                    end: newDatesState.endDate,
                    start: newDatesState.startDate,
                    periodType: newDatesState.periodType,
                });
                setRangeSelectedOption(
                    dropdownDateRangesOptions.find(
                        option =>
                            option.dateState === selectedRange.dateState &&
                            option.periodType === selectedRange.periodType
                    )
                );
                setState(prev => ({
                    ...prev,
                    predefinedSelectedDate: selectedRange.value,
                }));
            }
        },
        [onDatesChange, weekType]
    );

    const handleStartDateChange = useCallback(
        date => {
            onDatesChange({
                ...dates,
                start: date,
                dateState: customDropdownDateRangesOption.dateState,
                periodType: customDropdownDateRangesOption.periodType,
            });
            setRangeSelectedOption(customDropdownDateRangesOption);
            setState(prev => {
                return {
                    ...prev,
                    predefinedSelectedDate: predefinedOtherOption.value,
                };
            });
        },
        [dates, onDatesChange]
    );

    const handleEndDateChange = useCallback(
        date => {
            onDatesChange({
                ...dates,
                end: date,
                dateState: customDropdownDateRangesOption.dateState,
                periodType: customDropdownDateRangesOption.periodType,
            });
            setRangeSelectedOption(customDropdownDateRangesOption);
            setState(prev => ({
                ...prev,
                predefinedSelectedDate: predefinedOtherOption.value,
            }));
        },
        [dates, onDatesChange]
    );

    const handleAvailabilityClick = useCallback(() => {
        onBothFiltersChange({
            availability: predefinedAvailabilityOptions[0].values,
            utilization: EMPTY_VALUE,
        });

        setState(state => ({
            ...state,
            finderCase: 'availability',
            utilizationSelectedOption: null,
            availabilitySelectedOption: predefinedAvailabilityOptions[0].key,
        }));
    }, [onBothFiltersChange, predefinedAvailabilityOptions]);

    const handleUtilisationClick = useCallback(() => {
        onBothFiltersChange({
            availability: EMPTY_VALUE,
            utilization: predefinedUtilisationOptions[0].values,
        });

        setState(state => ({
            ...state,
            finderCase: 'utilization',
            utilizationSelectedOption: predefinedUtilisationOptions[0].key,
            availabilitySelectedOption: null,
        }));
    }, [onBothFiltersChange, predefinedUtilisationOptions]);

    const handlePredefinedAvailabilityClick = option => {
        const { values } = option;
        const method = isAvailabilityCase ? onAvailabilityChanged : onUtilizationChanged;

        setState(prev => ({
            ...prev,
            utilizationSelectedOption: isAvailabilityCase ? undefined : option.key,
            availabilitySelectedOption: isAvailabilityCase ? option.key : undefined,
        }));
        method(values);
    };

    const handleSliderChange = useCallback(
        value => {
            const method = isAvailabilityCase ? onAvailabilityChanged : onUtilizationChanged;
            method(value);
            setState(prev => ({
                ...prev,
                utilizationSelectedOption: null,
                availabilitySelectedOption: null,
            }));
        },
        [isAvailabilityCase, onAvailabilityChanged, onUtilizationChanged]
    );

    const sliderState = (isAvailabilityCase ? availability : utilization) ?? { operator: '', value: '' };

    const disabled = !isActive;
    const marks = isAvailabilityCase ? availabilitySliderConfig.marks : utilizationSliderConfig.marks;
    const sliderMin = isAvailabilityCase ? availabilitySliderConfig.min : utilizationSliderConfig.min;
    const sliderMax = isAvailabilityCase ? availabilitySliderConfig.max : utilizationSliderConfig.max;
    const sliderTitle = isAvailabilityCase ? 'Availability' : 'Utilization';

    const handleRangeSelectChange = useCallback(
        (event, value) => {
            handlePredefinedRangeClick(value);
            setState(prev => ({
                ...prev,
                rangeSelectedOption: value,
                predefinedSelectedDate: (
                    predefinedDateRangesOptions.find(
                        option => option.dateState === value.dateState && option.periodType === value.periodType
                    ) || predefinedOtherOption
                ).value,
            }));
        },
        [handlePredefinedRangeClick]
    );

    const availabilityText = useMemo(() => {
        let selectedOption;

        if (isAvailabilityCase) {
            selectedOption = predefinedAvailabilityOptions.find(
                option => option.key === state.availabilitySelectedOption
            );
        } else {
            selectedOption = predefinedUtilisationOptions.find(
                option => option.key === state.utilizationSelectedOption
            );
        }

        return selectedOption?.description || '';
    }, [
        isAvailabilityCase,
        predefinedAvailabilityOptions,
        predefinedUtilisationOptions,
        state.availabilitySelectedOption,
        state.utilizationSelectedOption,
    ]);

    return (
        <Layout stack gap="--Spacing-200" vAlign="center">
            <Layout hAlign="space-between">
                <Typography variant="title" size="large">
                    Find People based on:
                </Typography>
                <Layout vAlign="center" gap="--Spacing-200">
                    <Typography variant="label" size="medium">
                        Turn Capacity Finder On/Off
                    </Typography>
                    <Switch checked={isActive} onChange={handleToggleActive} dataCy="btn-switch" />
                </Layout>
            </Layout>
            <Divider />
            <Layout hAlign="center" gap={24}>
                <Chip
                    disabled={disabled}
                    onClick={handleUtilisationClick}
                    dataCy="capacity-filter-utilization"
                    label="Utilization %"
                    selected={!isAvailabilityCase}
                    startIcon={!isAvailabilityCase ? 'check' : undefined}
                />
                <Chip
                    disabled={disabled}
                    onClick={handleAvailabilityClick}
                    dataCy="capacity-filter-availability"
                    label="Available hours"
                    selected={isAvailabilityCase}
                    startIcon={isAvailabilityCase ? 'check' : undefined}
                />
            </Layout>
            <PredefinedDates
                variant={variant}
                onSelect={handlePredefinedRangeClick}
                selected={state.predefinedSelectedDate}
                disabled={disabled}
            />
            {state.predefinedSelectedDate === 'other' && (
                <Layout gap={24}>
                    <Select
                        dataCy="capacity-finder-date-range-buttons"
                        disableClearable
                        value={rangeSelectedOption}
                        onChange={handleRangeSelectChange}
                        options={dropdownDateRangesOptions}
                        label="Time range"
                        optionLabel="label"
                        optionValue="value"
                        disabled={disabled}
                    />
                    <DatePickerInput
                        disabled={disabled}
                        dataCy="capacity-finder-start-input"
                        name="capacity-finder-start-date"
                        label="Start date"
                        value={dates.start}
                        onChange={handleStartDateChange}
                        disabledDays={day => {
                            return isAfter(day, dates.end, 'day');
                        }}
                    />
                    <DatePickerInput
                        disabled={disabled}
                        dataCy="capacity-finder-end-input"
                        name="capacity-finder-end-date"
                        label="End date"
                        value={dates.end}
                        onChange={handleEndDateChange}
                        disabledDays={day => {
                            return isBefore(day, dates.start, 'day');
                        }}
                    />
                </Layout>
            )}

            {isAvailabilityCase ? (
                <ButtonsWrapper isAvailability={isAvailabilityCase} hAlign="space-between">
                    {predefinedAvailabilityOptions.map(option => {
                        return (
                            <UtilizationIconButton
                                noFill={true}
                                key={option.key}
                                label={option.label}
                                disabled={disabled || option.disabled}
                                availabilityKey={option.key}
                                selectedOption={state.availabilitySelectedOption}
                                icon={option.icon}
                                iconActive={option.iconActive}
                                dataCy={option.dataCy}
                                onClick={() => handlePredefinedAvailabilityClick(option)}
                            />
                        );
                    })}
                    <StyledIconButtonWrapper stack hAlign="center" gap="--Spacing-50">
                        <IconButton
                            dataCy="availability-option-custom"
                            disabled={disabled}
                            variant={state.availabilitySelectedOption === null ? 'filled' : 'outlined'}
                            icon="edit"
                            onClick={() => {
                                onBothFiltersChange({
                                    availability: DEFAULT_VALUE,
                                    utilization: EMPTY_VALUE,
                                });

                                setState(prev => ({
                                    ...prev,
                                    utilizationSelectedOption: null,
                                    availabilitySelectedOption: null,
                                }));
                            }}
                        />
                        <Typography variant="label" size="medium">
                            Custom
                        </Typography>
                    </StyledIconButtonWrapper>
                </ButtonsWrapper>
            ) : (
                <ButtonsWrapper hAlign="space-between">
                    {predefinedUtilisationOptions.map(option => {
                        return (
                            <UtilizationIconButton
                                key={option.key}
                                label={option.label}
                                disabled={disabled || option.disabled}
                                availabilityKey={option.key}
                                selectedOption={state.utilizationSelectedOption}
                                icon={option.icon}
                                dataCy={option.dataCy}
                                onClick={() => handlePredefinedAvailabilityClick(option)}
                            />
                        );
                    })}
                    <StyledIconButtonWrapper stack hAlign="center" gap="--Spacing-50">
                        <IconButton
                            dataCy="utilization-option-custom"
                            disabled={disabled}
                            variant={state.utilizationSelectedOption === null ? 'filled' : 'outlined'}
                            icon="edit"
                            onClick={() => {
                                onBothFiltersChange({
                                    availability: EMPTY_VALUE,
                                    utilization: DEFAULT_VALUE,
                                });

                                setState(prev => ({
                                    ...prev,
                                    utilizationSelectedOption: null,
                                    availabilitySelectedOption: null,
                                }));
                            }}
                        />
                        <Typography variant="label" size="medium">
                            Custom
                        </Typography>
                    </StyledIconButtonWrapper>
                </ButtonsWrapper>
            )}
            <Typography variant="label" size="medium" align="center">
                {availabilityText}
            </Typography>
            {((isAvailabilityCase && state.availabilitySelectedOption === null) ||
                (!isAvailabilityCase && state.utilizationSelectedOption === null)) && (
                <SliderFilterV2
                    key={isAvailabilityCase ? 'availability' : 'utilization'}
                    min={sliderMin}
                    max={sliderMax}
                    defaultValues={sliderState}
                    onChange={handleSliderChange}
                    marks={marks}
                    title={sliderTitle}
                    isAvailabilityCase={isAvailabilityCase}
                    disabled={disabled}
                    variant={variant}
                />
            )}
            {variant === 'scheduler' && <Divider />}
        </Layout>
    );
};

CapacityFinder.propTypes = {
    variant: PropTypes.oneOf(['scheduler', 'builder']),
    weekType: PropTypes.string,
    initialDatesState: PropTypes.object,
    onDatesChange: PropTypes.func.isRequired,
    onBothFiltersChange: PropTypes.func.isRequired,
    utilization: PropTypes.object,
    onUtilizationChanged: PropTypes.func.isRequired,
    availability: PropTypes.object,
    onAvailabilityChanged: PropTypes.func.isRequired,
};

const memoized = React.memo(CapacityFinder);

export { memoized as CapacityFinder };
